import { useCallback } from 'react';
import { useRelayEnvironment, type GraphQLTaggedNode } from 'react-relay';
import { useApolloClient } from '@apollo/react-hooks';
import { type DocumentNode } from 'graphql';
import { type IEnvironment, type ReaderSelection } from 'relay-runtime';

import { toDocumentNode } from './toDocumentNode';

export type UseWriteFragmentInteropArgs = {
	fragmentSpec: GraphQLTaggedNode | DocumentNode;
};

export type UseWriteFragmentInteropResult<TData> = (params: {
	fragmentId: string;
	data: TData;
}) => void;

function useWriteFragmentInteropRelay<TData>(
	args: UseWriteFragmentInteropArgs,
): UseWriteFragmentInteropResult<TData> {
	const { fragmentSpec } = args;
	const env = useRelayEnvironment();

	return useCallback(
		({ data, fragmentId }) => {
			// @ts-expect-error TODO: should be removed once we only accept `GraphQLTaggedNode`
			writeFragmentShim(env, fragmentSpec, fragmentId, data);
		},
		[env, fragmentSpec],
	);
}

function useWriteFragmentInteropApollo<TData>(
	args: UseWriteFragmentInteropArgs,
): UseWriteFragmentInteropResult<TData> {
	const { fragmentSpec } = args;
	const client = useApolloClient();

	return useCallback(
		({ data, fragmentId }) => {
			client.writeFragment<TData>({
				id: fragmentId,
				fragment: toDocumentNode(fragmentSpec),
				data,
			});
		},
		[client, fragmentSpec],
	);
}

export function useWriteFragmentInterop<TData>(
	args: UseWriteFragmentInteropArgs,
): UseWriteFragmentInteropResult<TData> {
	return process.env.BUILD_VARIANT === 'relay'
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useWriteFragmentInteropRelay(args)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useWriteFragmentInteropApollo(args);
}

function apolloFragmentIdToRelayFragmentId(apolloFragmentId: string) {
	return apolloFragmentId.replace(/^\$ROOT_QUERY\./, 'client:root:');
}

/**
 * Shim function that mimics Apollo's `writeFragment` using Relay
 */
function writeFragmentShim<TData>(
	environment: IEnvironment,
	fragment: GraphQLTaggedNode,
	fragmentId: string,
	data: TData,
): void {
	if (!data) {
		return;
	}

	const relayFragmentId = apolloFragmentIdToRelayFragmentId(fragmentId);

	// Use commitUpdate to write to the store
	environment.commitUpdate((storeProxy) => {
		const record = storeProxy.get(relayFragmentId);
		if (!record) {
			return;
		}

		// Define a function to write the fields specified in the fragment
		const writeFields = (
			recordId: string,
			fields: readonly ReaderSelection[],
			writeData: any,
		): void => {
			const targetRecord = storeProxy.get(recordId);
			if (!targetRecord || !writeData) {
				return;
			}

			for (const field of fields) {
				if (field.kind === 'LinkedField') {
					// For linked fields, we need to handle nested data
					const linkedData = writeData[field.name];
					if (linkedData) {
						const linkedRecord = targetRecord.getLinkedRecord(field.name);
						if (linkedRecord) {
							writeFields(linkedRecord.getDataID(), field.selections, linkedData);
						}
					}
				} else if (field.kind === 'ScalarField') {
					if (writeData[field.name] !== undefined) {
						targetRecord.setValue(writeData[field.name], field.name);
					}
				} else {
					throw new Error('Unsupported field');
				}
			}
		};

		const writerFragment = fragment as any;
		const topLevelFields = writerFragment?.selections;

		if (topLevelFields) {
			writeFields(relayFragmentId, topLevelFields ?? [], data);
		}
	});
}
